import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Service e Modelli (Generati da specifica OpenAPI)
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { ResponseSubmission } from '../../core/models/models';
import { MessageService } from 'primeng/api';

// PrimeNG Components (Sintassi 2026)
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';

@Component({
  selector: 'app-questionnaire-run-bo',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    Toast,
    InputNumber,
    DatePicker,
  ],
  providers: [MessageService],
  templateUrl: './questionnaire-run-bo.component.html',
  styleUrls: ['./questionnaire-run-bo.component.scss'],
})
export class QuestionnaireRunBoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(QuestionnaireService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);

  // Identificativo univoco della sessione di compilazione
  private readonly sessionInstanceId: string = crypto.randomUUID();

  // Stato reattivo con Signals
  form: FormGroup = this.fb.group({});
  questionnaire = signal<any>(null);
  isLoading = signal(true);
  isSubmitting = signal(false);

  ngOnInit(): void {
    this.titleService.setTitle('Visualizzazione Questionario - Portale Fiduciari AXA');
    this.loadQuestionnaire();
  }

  private loadQuestionnaire(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "ID Questionario mancante nell'URL",
      });
      this.isLoading.set(false);
      return;
    }

    this.service.getQuestionnaireById(id).subscribe({
      next: (data) => {
        this.questionnaire.set(data);
        this.buildForm(data.questions);
        this.isLoading.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Caricamento fallito',
        });
        this.isLoading.set(false);
      },
    });
  }

  private buildForm(questions: any[]): void {
    // Reset totale per evitare residui di vecchi questionari in memoria
    this.form = this.fb.group({});
    const group: any = {};

    questions.forEach((q) => {
      // IMPORTANTE: q.id deve essere l'UUID del backend
      const questionId = q.id;

      // WCAG FIX: All question types respect mandatory flag
      const validators = q.mandatory ? [Validators.required] : [];

      let initialValue: any = q.type === 'MULTISELECT' ? [] : q.type === 'BOOLEAN' ? null : '';

      // Creiamo il controllo usando l'ID originale come chiave
      group[questionId] = new FormControl(initialValue, validators);

      if (q.type === 'SINGLESELECT') {
        // Usiamo il suffisso solo per il campo di testo di supporto
        group[`${questionId}_otherText`] = new FormControl('');
      }
    });

    this.form = this.fb.group(group);
  }

  // --- LOGICA SELEZIONE ---

  isOptionChecked(questionId: string, optionId: string): boolean {
    const values = this.form.get(questionId)?.value;
    return Array.isArray(values) && values.includes(optionId);
  }

  onMultiChoiceChange(questionId: string, optionId: string, isChecked: boolean): void {
    const control = this.form.get(questionId);
    let currentValues: any[] = Array.isArray(control?.value) ? [...control?.value] : [];

    if (isChecked) {
      if (!currentValues.includes(optionId)) currentValues.push(optionId);
    } else {
      currentValues = currentValues.filter((v) => v !== optionId);
    }

    control?.setValue(currentValues);
    control?.markAsDirty();
    control?.updateValueAndValidity({ emitEvent: true });
  }

  // --- HELPERS TEMPLATE ---

  getControl(id: string): FormControl {
    return this.form.get(id) as FormControl;
  }

  isQuestionType(question: any, type: string): boolean {
    return question.type === type;
  }

  isOtherSelected(questionId: string): boolean {
    return this.form.get(questionId)?.value === 'SPECIAL_OTHER';
  }

  closeQuestionnaire(): void {
    this.router.navigate(['/questionnaires']);
  }
}
